import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../api/client.js';

const AuthContext = createContext(null);

// Backend pakai session cookie (express-session, httpOnly, bukan JWT) — jadi
// TIDAK ada token yang perlu disimpan di localStorage. Status login selalu
// ditentukan dengan menanyakan ke server (GET /auth/me), yang otomatis ikut
// cookie session lewat `withCredentials: true` di api client. Sebelumnya
// context ini menunggu token di localStorage dulu sebelum mau bertanya ke
// server; karena backend tidak pernah mengirim token, refresh halaman selalu
// dianggap logout walau cookie session-nya masih valid.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data?.loggedIn ? data.user : null);
      setError(null);
    } catch (err) {
      setUser(null);
      if (err.response?.status !== 401) {
        setError(err.response?.data?.message || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (username, password, captcha) => {
    try {
      setLoading(true);
      setError(null);

      const payload = { username: username.trim(), password: password.trim() };
      if (captcha) payload.captcha = captcha.trim();

      const { data } = await api.post('/auth/login', payload);

      if (data.ok && data.user) {
        setUser(data.user);
        setError(null);
        return { success: true, data };
      }
      throw new Error(data.message || 'Login failed');
    } catch (err) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message || 'Login failed';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        needsCaptcha: errorData?.needsCaptcha || false,
        response: errorData,
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
    }
  };

  const updateUser = (updatedUser) => setUser(updatedUser);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refresh,
    updateUser,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
